import { setFileversionIdAndNodeOffsetIdMap } from "../reducers/common";
import { selectFileVersionData } from "../reducers/fileVersion";
import { store } from "../ProviderApp";
import { getAllLeafNodesRecur, CubeGlobal } from "@Features/viewer-core";
import { isEqual } from "lodash";
import { getIsViewerDependencyLoaded } from "./utils/helpers";
import { getNodeIdAndUniqueIdMapEntry, getLoadedNodeIdAndUniqueIdFileVersionIds } from "./utils/nodeIdAndUniqueIdMapDB";
import { InstanceModifier, ElementType, Matrix, Camera, Color } from "@ts3d-hoops/web-viewer";

/** fileVersionId → the HOOPS node-id offset of that file's model, as stored in the redux slice. */
type NodeOffsetMap = Record<string, number>;

export function getFileVersionIdAndOffSetByDesignViewerNodeId(nodeId: number) {
  const state = store.getState();
  const { fileversionIdAndNodeOffsetIdMap } = state;
  let offSet = -1;
  let fileVersionId: string | null = null;
  for (const key of Object.keys(fileversionIdAndNodeOffsetIdMap)) {
    const nodeOffset = fileversionIdAndNodeOffsetIdMap[key];
    if (nodeOffset !== undefined && nodeOffset <= nodeId && nodeOffset > offSet) {
      fileVersionId = key;
      offSet = nodeOffset;
    }
  }
  return { offSet, fileVersionId };
}

export const getFileversionIdFromAwsfilename = (awsFileName: string): string => {
  const state = store.getState();

  const { filesData } = state;
  const fileVersionId: string | undefined = Object.keys(filesData).find(
    (file) => filesData[file]?.awsFileName == awsFileName,
  );
  if (fileVersionId) return fileVersionId;
  return "";
};

export const getFileVersionIdFromFileId = (fileId: string | undefined) => {
  const state = store.getState();
  const { filesData, fileversionIdAndNodeOffsetIdMap } = state;
  const matches = Object.keys(filesData).filter((file) => filesData[file]?.fileId == fileId);
  return matches.find((id) => fileversionIdAndNodeOffsetIdMap[id] !== undefined) ?? matches[0];
};

export const getFileIdFromFileVersionId = (fileVersionId: string) => {
  const state = store.getState();
  const { filesData } = state;
  return filesData[fileVersionId]?.fileId;
};

export const getCubeUniqueIdFromNodeId = (nodeId: number) => {
  const relativeNodeId = nodeId - CubeGlobal.hwv.model.getNodeIdOffset(nodeId);
  const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(nodeId));
  if (fileVersionId) {
    const fileId = getFileIdFromFileVersionId(fileVersionId);
    const fileNodeMaps = getNodeIdAndUniqueIdMapEntry(fileVersionId);
    if (fileNodeMaps) {
      const cubeUniqueId = fileNodeMaps.nodeIdMap?.[relativeNodeId];
      if (cubeUniqueId) {
        return `${fileId}_${cubeUniqueId}`;
      } else {
        return `${fileId}_${relativeNodeId}`;
      }
    } else {
      return `${fileId}_${relativeNodeId}`;
    }
  }
  return false;
};

export const getNodeIdsFromCubeUniqueId = (
  tempCubeUniqueId: string,
  mapper?: NodeOffsetMap,
  sourceFileVersionId?: string,
) => {
  if (mapper == undefined) {
    mapper = getFileversionIdAndNodeOffsetIdMap();
  }

  const cubeUniqueId = tempCubeUniqueId.split("_").splice(1).join("_");
  const fileId = tempCubeUniqueId.split("_")[0];

  let nodeIds: number[] = [];

  let isFileFound = false;

  for (const fileVersionId of getLoadedNodeIdAndUniqueIdFileVersionIds()) {
    const tempFileId = getFileIdFromFileVersionId(fileVersionId);
    if (tempFileId == fileId) {
      isFileFound = true;
      nodeIds = getNodeIdAndUniqueIdMapEntry(fileVersionId)?.cubeUniqueIdMap[cubeUniqueId] ?? [];
      if (Array.isArray(nodeIds) && nodeIds.length) {
        // KNOWN BUG, preserved verbatim: a file that is loaded in HOOPS but missing from the
        // offset map yields `undefined` here and turns every node id into NaN. The `!` is erased.
        const nodeOffset = mapper[fileVersionId]!;
        nodeIds = nodeIds.map((node) => node + nodeOffset);
      } else {
        nodeIds = [];
      }
    }
  }

  if (!isFileFound && sourceFileVersionId) {
    // Same unguarded lookup as above, preserved verbatim.
    const nodeOffset = mapper[sourceFileVersionId]!;
    nodeIds.push(Number(cubeUniqueId) + nodeOffset);
  }

  return nodeIds;
};

export const handleModelReset = ({
  viewerImpl,
  isModelStructureReady,
  isSettingsApplied,
}: {
  // `viewerImpl` stays `any`, and it is load-bearing: HoopsViewerImpl exposes `hwv` (and every
  // other member) only through its deliberate `[key: string]: any` index signature, and
  // `getIsViewerDependencyLoaded` declares the same slot `any`. See ViewerImpl.tsx.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- HoopsViewerImpl's `[key: string]: any` is deliberate
  viewerImpl: any;
  isModelStructureReady: boolean;
  isSettingsApplied: boolean;
  /** Both call sites also pass `hwv`; it is unused here — the guard re-reads it off CubeGlobal. */
  hwv?: unknown;
}) => {
  if (getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
    viewerImpl.hwv.model.reset();
    viewerImpl.hwv.model.setInstanceModifier(
      InstanceModifier.DoNotSelect,
      [viewerImpl.hwv.model.getAbsoluteRootNode()],
      false,
    );
    viewerImpl.hwv.model.resetNodesColor([viewerImpl.hwv.model.getAbsoluteRootNode()]);
    viewerImpl.hwv.model.setInstanceModifier(
      InstanceModifier.DoNotXRay,
      [viewerImpl.hwv.model.getAbsoluteRootNode()],
      false,
    );
  }
};

export const getFileversionIdAndNodeOffsetIdMap = (fileVersionId?: string) => {
  const state = store.getState();
  const { fileversionIdAndNodeOffsetIdMap, filesData, getAccessPermission } = state;

  // Holds `undefined` in the single-file branch when the caller passed no id — see the
  // `every` guard below, which then simply misses. Both branches assign, so no initialiser.
  let fileVersions: (string | undefined)[];

  // Read through the slice's own selector: it handles the absent id (no file mounted) and keeps
  // both fields below at their declared types, which a runtime-built `_.get` path could not.
  const fileEntry = selectFileVersionData(filesData, fileVersionId);
  if (fileEntry?.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
    // The mapper below is built only from models actually loaded into HOOPS, so the
    // short-circuit must compare against the LOADABLE constituents. Counting deleted,
    // unconverted or permission-restricted ones would make it permanently false and
    // re-walk the whole node tree on every call (this runs from per-node handlers).
    // SCHEMA GAP: the accessPermission slice state is the `{}` inferred from its initialState, so
    // the isFile403 fields it really holds are undeclared.
    const access =
      (getAccessPermission as { fileVersionId?: string; files?: Record<string, { canViewFile?: boolean }> })
        .fileVersionId === fileVersionId
        ? (getAccessPermission as { files?: Record<string, { canViewFile?: boolean }> })
        : undefined;
    fileVersions = (fileEntry.sourceFileVersionId ?? [])
      .filter(
        (file) =>
          !file.isDeleted && file.conversionStatus === "COMPLETED" && access?.files?.[file.id]?.canViewFile !== false,
      )
      .map((file) => file.id);
  } else {
    fileVersions = [fileVersionId];
  }

  const mappedFileVersionIds = new Set(Object.keys(fileversionIdAndNodeOffsetIdMap));
  // `val!` is erased: a missing id is passed through verbatim and `Set.has` simply reports false.
  if (fileVersions.every((val) => mappedFileVersionIds.has(val!))) {
    return fileversionIdAndNodeOffsetIdMap;
  }

  const mapper: NodeOffsetMap = {};

  const absoluteNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
  const modelFileNodes = CubeGlobal.hwv.model.getNodeChildren(absoluteNode);

  for (const node of modelFileNodes) {
    const awsFilename = CubeGlobal.hwv.model.getModelFileNameFromNode(node);
    const fileVersionId = getFileversionIdFromAwsfilename(awsFilename);
    if (fileVersionId && !mapper[fileVersionId]) {
      const nodeOffset = CubeGlobal.hwv.model.getNodeIdOffset(node);
      mapper[fileVersionId] = nodeOffset;
    }
  }
  if (!isEqual(fileversionIdAndNodeOffsetIdMap, mapper)) {
    store.dispatch(setFileversionIdAndNodeOffsetIdMap(mapper));
  }
  return mapper;
};

export const modelTransfroms = {
  toJson: () => {
    // `[fileVersionId, Matrix.toJson()]` pairs; the matrix side stays opaque because
    // `CubeGlobal` (and therefore `hwv.model`) is untyped by design.
    const modelVsMatrixMap: [string, unknown][] = [];
    const absRootNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
    const modelNodes = CubeGlobal.hwv.model.getNodeChildren(absRootNode);
    for (const modelNode of modelNodes) {
      const modelTransform = CubeGlobal.hwv.model.getNodeMatrix(modelNode);
      const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(modelNode));
      modelVsMatrixMap.push([fileVersionId, modelTransform.toJson()]);
    }

    return modelVsMatrixMap;
  },
  fromJson: async (modelVsMatrixMapjson: [string, unknown][]) => {
    // If the model is a drawing don't reset the transform, transforms works well only for 3d models.
    const sheetIds = CubeGlobal.hwv.sheetManager.getSheetIds();
    if (sheetIds.length > 0) return;

    for (const element of modelVsMatrixMapjson) {
      const fileVersionIdinput = element[0];
      const matrix = Matrix.fromJson(element[1]);
      const absRootNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
      const modelNodes = CubeGlobal.hwv.model.getNodeChildren(absRootNode);

      for (const modelNode of modelNodes) {
        const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(modelNode));
        if (fileVersionIdinput == fileVersionId) {
          CubeGlobal.hwv.model.setNodeMatrix(modelNode, matrix);
        }
      }
    }
  },
};

export const cubeCuttingData = {
  toJson: () => CubeGlobal.hwv.cuttingManager.toJson(),
  fromJson: async (cuttingPlaneJson: unknown) => await CubeGlobal.hwv.cuttingManager.fromJson(cuttingPlaneJson),
};

export const cubeCameraData = {
  toJson: () => CubeGlobal.hwv.view.getCamera().toJson(),
  fromJson: (cameraJson: unknown) => CubeGlobal.hwv.view.setCamera(Camera.fromJson(cameraJson), 400),
};

export const cubeColorData = {
  toJson: async () => {
    // `[cubeUniqueId, Color.toJson()]` pairs; `Color.fromJson` takes the second element back as
    // `unknown`, so it is never narrowed here.
    const colors: [string, unknown][] = [];
    const colorMap = await CubeGlobal.hwv.model.getNodeColorMap(
      CubeGlobal.hwv.model.getAbsoluteRootNode(),
      ElementType.Faces,
    );
    for (const [a, b] of colorMap) {
      const data = getCubeUniqueIdFromNodeId(a);
      if (data) {
        colors.push([data, b.toJson()]);
      }
    }
    return colors;
  },
  // The color from json is getting parsed and activated at view point itself
  fromJson: (colorsJson: [string, unknown][]) => {
    const colorMap = new Map();
    for (const val of colorsJson) {
      colorMap.set(val[0], Color.fromJson(val[1]));
    }
    CubeGlobal.hwv.model.setNodesColors(colorMap);
  },
};

export const cubeTransparencyData = {
  toJson: () => {
    const state = store.getState();
    const { transparentElements, GetSettingModalData } = state;
    let transparentCubeUniqueIdElements = transparentElements.map((nodeId) => getCubeUniqueIdFromNodeId(nodeId));
    transparentCubeUniqueIdElements = transparentCubeUniqueIdElements.filter(Boolean);

    return {
      elements: transparentCubeUniqueIdElements,
      value: GetSettingModalData.General_transparency,
    };
  },
  fromJson: (data: { elements: unknown; value: number }) => {
    CubeGlobal.hwv.model.setNodesOpacity(data.elements, 1 - data.value);
  },
};

export const cubeVisibilityData = {
  toJson: async () => {
    const visibilityExceptions: string[] = [];

    const visibilityData = await CubeGlobal.hwv.model.getVisibilityState(CubeGlobal.hwv.model.getAbsoluteRootNode());

    for (const val of visibilityData.visibilityExceptions) {
      const cubeUniqueId = getCubeUniqueIdFromNodeId(val);
      if (cubeUniqueId) {
        visibilityExceptions.push(cubeUniqueId);
      }
    }

    const { defaultVisibility } = visibilityData;
    return {
      visibilityExceptions,
      defaultVisibility,
    };
  },

  fromJson: async (defaultVisibility: boolean, visibilityExceptions: unknown) => {
    await CubeGlobal.hwv.model.setNodesVisibility([CubeGlobal.hwv.model.getAbsoluteRootNode()], defaultVisibility);
    await CubeGlobal.hwv.model.setNodesVisibility(visibilityExceptions, !defaultVisibility);
  },
};

export const cubeMagnitudeData = {
  toJson: () => CubeGlobal.hwv.explodeManager.getMagnitude(),
  fromJson: async (magnitude: number) => await CubeGlobal.hwv.explodeManager.setMagnitude(magnitude),
};

export const cubeFaceVisibility = {
  toJson: () => CubeGlobal.hwv.view.getFaceVisibility(),
  fromJson: async (faceVisibility: boolean) => await CubeGlobal.hwv.view.setFaceVisibility(faceVisibility),
};

export const cubeSnapshot = {
  toJson: async () => {
    const hwv = CubeGlobal.hwv;
    const prev = hwv.getAllowHighDpi();
    hwv.setAllowHighDpi(false);
    try {
      const snapshot = await hwv.takeSnapshot();
      return snapshot.src;
    } finally {
      hwv.setAllowHighDpi(prev);
    }
  },
};

export const cubeLineVisibility = {
  toJson: () => CubeGlobal.hwv.view.getLineVisibility(),
  fromJson: async (lineVisibility: boolean) => await CubeGlobal.hwv.view.setLineVisibility(lineVisibility),
};

export const cubeSheetId = {
  toJson: () => CubeGlobal.hwv.sheetManager.getActiveSheetId(),
  fromJson: async (sheetId: number | undefined) => {
    if (sheetId && sheetId != 0) {
      await CubeGlobal.hwv.sheetManager.setActiveSheetId(sheetId);
    }
  },
};

export const cubeMeasurementsMarkup = {
  toJson: () => CubeGlobal.hwv.measureManager.exportMarkup(),
  fromJson: async (measurements: unknown) => await CubeGlobal.hwv.measureManager.loadData(measurements),
};

export const cubeMarkup = {
  toJson: () => {
    let markup = [];
    if (CubeGlobal.hwv.markupManager.getActiveMarkupView(CubeGlobal.hwv.view)) {
      const markupJson = CubeGlobal.hwv.markupManager.getActiveMarkupView(CubeGlobal.hwv.view).toJson();
      markup = markupJson.markup;
    }
    return markup;
  },
  fromJson: async (markupJson: { uniqueId?: unknown; markup?: unknown }) => {
    // In case issues doesnot have a viewpoint
    const viewerImpl = CubeGlobal.viewerImpl;
    if (Object.keys(markupJson).length) {
      await CubeGlobal.hwv.markupManager.loadMarkupData({ views: [markupJson] });
      await CubeGlobal.hwv.markupManager.activateMarkupViewWithPromise(markupJson.uniqueId, CubeGlobal.hwv.view);
      viewerImpl.loadCustomRedlineMarkups(markupJson.markup);
    }
  },
};

/** One HOOPS selection item, as far as this walker is concerned. */
interface HoopsSelectionItem {
  _nodeId: number;
  toJson(): object;
}

export function handleSelectionLeafNode(selectionsData: HoopsSelectionItem[]) {
  const viewerImpl = CubeGlobal.viewerImpl;
  const results: Record<string, unknown>[] = [];
  for (const selection of selectionsData) {
    const nodeId = selection._nodeId;
    if (CubeGlobal.hwv.model.getNodeChildren(nodeId).length) {
      const leafNodes: number[] = [];
      getAllLeafNodesRecur(nodeId, leafNodes);
      for (const node of leafNodes) {
        const data = getCubeUniqueIdFromNodeId(node);
        const name = CubeGlobal.hwv.model.getNodeName(nodeId);
        if (data) {
          results.push({
            ...selection.toJson(),
            nodeId: data,
            name,
          });
        }
      }
    } else {
      const data = getCubeUniqueIdFromNodeId(nodeId);
      const name = viewerImpl.hwv.model.getNodeName(nodeId);
      if (data) {
        results.push({
          ...selection.toJson(),
          nodeId: data,
          name,
        });
      }
    }
  }
  return results;
}

export const cubeSelectionData = {
  toJson: () => {
    const selectionsData = CubeGlobal.hwv.selectionManager.getResults();
    return handleSelectionLeafNode(selectionsData);
  },
  fromJson: (selectionJson: unknown) => {
    CubeGlobal.hwv.selectionManager.loadSelectionData(selectionJson);
  },
};

export const cubeCurrentShadingData = {
  toJson: () => CubeGlobal.hwv.view.getDrawMode(),
  fromJson: (mode: unknown) => CubeGlobal.hwv.view.setDrawMode(mode),
};
